/** Action catalog for Mends AI — navigate / help / propose mutation. */

/** Payload do `navigate` para as regras de follow-up. */
export const FOLLOW_UP_NAVIGATE_PAYLOAD = '{"path":"/follow-up"}';

export type ActionKind = 'nav' | 'action' | 'mutation' | 'help' | 'contact';

export interface AiActionDef {
  id: string;
  kind: ActionKind;
  title: string;
  subtitle: string;
  /** route path, action:*, or mutation:* */
  path: string;
  keywords: string[];
  requiresConfirm: boolean;
  approvalTemplate?: {
    title: string;
    body: string;
    suggestedAction: string;
    actionKind?: string;
    actionPayload?: string;
  };
}

export const QUICK_ACTIONS: AiActionDef[] = [
  {
    id: 'nav-today',
    kind: 'nav',
    title: 'Abrir Hoje',
    subtitle: 'Computador de bordo — o que importa agora',
    path: '/hoje',
    keywords: ['today', 'hoje', 'priority', 'start', 'bordo'],
    requiresConfirm: false,
  },
  {
    id: 'nav-chat',
    kind: 'nav',
    title: 'Ir para Conversas',
    subtitle: 'Inbox WhatsApp',
    path: '/chat',
    keywords: ['conversations', 'chat', 'inbox', 'whatsapp', 'messages', 'conversas'],
    requiresConfirm: false,
  },
  {
    id: 'nav-opportunities',
    kind: 'nav',
    title: 'Ver oportunidades',
    subtitle: 'Radar de leads',
    path: '/central-inteligencia?tab=oportunidades',
    keywords: ['opportunities', 'pipeline', 'funnel', 'leads', 'oportunidades'],
    requiresConfirm: false,
  },
  {
    id: 'nav-central',
    kind: 'nav',
    title: 'Abrir Central de Inteligência',
    subtitle: 'Análises · Riscos · Recomendações · Aprovações',
    path: '/central-inteligencia',
    keywords: ['intelligence', 'briefing', 'central', 'inteligencia'],
    requiresConfirm: false,
  },
  {
    id: 'nav-approvals',
    kind: 'nav',
    title: 'Ir para Aprovações',
    subtitle: 'Fila — Ok executa',
    path: '/central-inteligencia?tab=aprovacoes',
    keywords: ['approvals', 'aprovacoes', 'queue', 'ok', 'fila'],
    requiresConfirm: false,
  },
  {
    id: 'nav-clients',
    kind: 'nav',
    title: 'Ir para Clientes',
    subtitle: 'Lista de contatos',
    path: '/contacts',
    keywords: ['clients', 'contacts', 'list', 'clientes', 'contatos'],
    requiresConfirm: false,
  },
  {
    id: 'nav-agents',
    kind: 'nav',
    title: 'Go to Agents',
    subtitle: 'AI configuration',
    path: '/ai-agent',
    keywords: ['agents', 'agent', 'configure'],
    requiresConfirm: false,
  },
  {
    id: 'nav-followup',
    kind: 'nav',
    title: 'Go to Follow-up',
    subtitle: 'Rules and status',
    path: '/follow-up',
    keywords: ['follow-up', 'followup', 'reactivate'],
    requiresConfirm: false,
  },
  {
    id: 'action-voice',
    kind: 'action',
    title: 'Registrar acontecimento',
    subtitle: 'Ligação, reunião, retorno — mundo real na operação',
    path: 'action:register-voice',
    keywords: ['register', 'voice', 'record', 'meeting', 'call', 'mic', 'registrar', 'gravar'],
    requiresConfirm: false,
  },
  {
    id: 'mutation-followup',
    kind: 'mutation',
    title: 'Agendar follow-up',
    subtitle: 'Confirme e abra as regras de follow-up',
    path: 'mutation:follow-up',
    keywords: ['schedule follow', 'create follow', 'follow-up', 'followup'],
    requiresConfirm: true,
    approvalTemplate: {
      title: 'Agendar follow-up',
      body: 'A MENDS propôs um follow-up. Ok abre Follow-up para confirmar as regras.',
      suggestedAction: 'Abrir Follow-up e confirmar',
      // Só abre as regras de follow-up: navegação, não mensagem ao cliente.
      actionKind: 'navigate',
      actionPayload: FOLLOW_UP_NAVIGATE_PAYLOAD,
    },
  },
  {
    id: 'mutation-briefing',
    kind: 'mutation',
    title: 'Generate sector briefing',
    subtitle: 'Ok generates the briefing now',
    path: 'mutation:briefing',
    keywords: ['generate briefing', 'briefing', 'daily summary'],
    requiresConfirm: true,
    approvalTemplate: {
      title: 'Generate sector briefing',
      body: 'Generate a commercial briefing for this connection. Ok runs it now.',
      suggestedAction: 'Generate briefing',
      actionKind: 'generate_briefing',
      actionPayload: '{}',
    },
  },
  {
    id: 'mutation-memory',
    kind: 'mutation',
    title: 'Refresh contact memory',
    subtitle: 'Needs an open contact or name',
    path: 'mutation:refresh-memory',
    keywords: ['memory', 'refresh memory', 'contact facts'],
    requiresConfirm: true,
    approvalTemplate: {
      title: 'Refresh contact memory',
      body: 'Regenerate structured contact memory from recent messages.',
      suggestedAction: 'Refresh memory',
      actionKind: 'refresh_memory',
    },
  },
];

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function matchActions(query: string, limit = 8): AiActionDef[] {
  const q = normalize(query);
  if (!q) return QUICK_ACTIONS.filter((a) => a.kind === 'nav' || a.id === 'action-voice').slice(0, 8);

  const scored = QUICK_ACTIONS.map((action) => {
    let score = 0;
    const titleN = normalize(action.title);
    if (titleN.includes(q)) score += 10;
    for (const kw of action.keywords) {
      const k = normalize(kw);
      if (q.includes(k) || k.includes(q)) score += 6;
      else if (q.split(/\s+/).some((w) => w.length > 2 && (k.includes(w) || w.includes(k)))) score += 2;
    }
    if (/\b(go to|open|show|take me|navegar|ir para|abrir)\b/.test(q) && action.kind === 'nav' && score > 0) {
      score += 3;
    }
    if (/\b(schedule|create|generate|agendar|marcar|criar|gerar)\b/.test(q) && action.kind === 'mutation' && score > 0) {
      score += 4;
    }
    return { action, score };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((x) => x.action);
}

export function kindLabel(kind: string): string {
  switch (kind) {
    case 'nav':
      return 'Navigate';
    case 'action':
      return 'Action';
    case 'mutation':
      return 'Approve';
    case 'help':
      return 'Help';
    case 'contact':
      return 'Contact';
    default:
      return kind;
  }
}

export function extractContactHint(query: string): string | null {
  const q = query.trim();
  const patterns = [
    /(?:open\s+)?(?:chat|conversation|contact)\s+(?:with|for|of)\s+(.+)/i,
    /(?:abrir\s+)?conversa\s+(?:do|da|de)\s+(.+)/i,
    /(?:send|enviar|draft).+?(?:to|para)\s+(.+?)(?:\s*[:\-–]|$)/i,
    /(?:summarize|map pains?|map interests?)\s+(?:for\s+)?(.+)/i,
  ];
  for (const p of patterns) {
    const m = q.match(p);
    if (m?.[1]) {
      let name = m[1].replace(/[?.!]+$/, '').trim();
      if (name.includes(':')) name = name.split(':')[0].trim();
      if (name.length >= 2) return name;
    }
  }
  return null;
}

/** Infer actionKind for createApproval from path. */
/**
 * `mutation:follow-up` só abre as regras de follow-up (o executor legado de follow_up apenas navegava):
 * vira `navigate` para /follow-up, nunca uma pergunta de texto ao vendedor.
 */
/** Payload do `navigate` usado por `mutation:follow-up`. */
export function actionPayloadFromPath(path: string): string | null {
  return path === 'mutation:follow-up' ? FOLLOW_UP_NAVIGATE_PAYLOAD : null;
}

export function actionKindFromPath(path: string): string | null {
  if (path === 'mutation:briefing') return 'generate_briefing';
  if (path === 'mutation:follow-up') return 'navigate';
  if (path === 'mutation:refresh-memory') return 'refresh_memory';
  if (path === 'mutation:send-message') return 'send_message';
  if (path === 'mutation:transfer-conversation') return 'transfer_conversation';
  return null;
}
